import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import styled from 'styled-components';
import axios from 'axios';
import { filteredAnalysesQuery, geneSymbolsQuery } from './queryStates';
import { axaouDevUrl } from './Query';
import { v4 as uuidv4 } from 'uuid';
import { selectedAnalyses } from './sharedState';
import { useAppNavigation } from './hooks/useAppNavigation';
import { AnalysisMetadata, GeneSymbol } from './types';
import { ColorMarker } from './UserInterface';
import { getCategoryFromConsequence, getLabelForConsequenceTerm } from './vepConsequences';
import { getConsequenceColor } from './VariantList/variantTableColumns';
import { variantGreenThreshold, variantYellowThreshold, greenThresholdColor, yellowThresholdColor } from './PhenotypeList/Utils';
import { mobileControlContract } from './browserUiContracts';

export const SearchBarContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: ${(props) => props.theme.surfaceAlt};
  border-radius: 10px;
  padding: 8px 16px;
  width: 100%;
`;

const Input = styled.input`
  border: none;
  outline: none;
  flex-grow: 1;
  min-width: 0;
  background: transparent;
  font-size: 16px;
  color: ${(props) => props.theme.text};
  text-overflow: ellipsis;

  &::placeholder {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    font-size: 13px;
  }
`;

const Icon = styled.span`
  margin-right: 8px;
  color: ${(props) => props.theme.text};
  cursor: pointer;
`;

const CloseIcon = styled.span`
  margin-left: auto;
  cursor: pointer;
  color: ${(props) => props.theme.text};
`;

export const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${(props) => props.theme.surfaceAlt};
  z-index: 1000;
  width: 60vw;
  max-height: 40vh;
  overflow-y: auto;
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  padding: 16px;
  color: ${(props) => props.theme.text};
  font-size: 16px;

  @media (max-width: 750px) {
    box-sizing: border-box;
    width: calc(100vw - 24px);
    max-height: calc(100dvh - 24px);
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  z-index: 999;
`;

export const CloseModalButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: ${(props) => props.theme.text};
`;

const SectionTitle = styled.h3`
  margin-top: 16px;
  margin-bottom: 8px;
  color: ${(props) => props.theme.text};
`;

const QueryBox = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 5px;
  padding: 8px;
  margin-bottom: 16px;
  background-color: ${(props) => props.theme.surface};
`;

const Magnifier = styled.span`
  margin-right: 8px;
  color: ${(props) => props.theme.text};
`;

const ModalInput = styled.input`
  border: none;
  outline: none;
  flex-grow: 1;
  background: transparent;
  font-size: 16px;
  color: ${(props) => props.theme.text};
`;

const VariantId = styled.span`
  font-family: monospace;
  font-weight: 600;
  font-size: 13px;
`;

const GeneSymbolLabel = styled.span`
  font-style: italic;
  font-weight: 600;
  color: #3279b7;
`;

const ConsequenceLabel = styled.span`
  font-size: 13px;
`;

const AssocInfo = styled.span`
  font-size: 12px;
  opacity: 0.75;
`;

const Separator = styled.span`
  margin: 0 6px;
  opacity: 0.4;
`;

interface SearchChoice {
  resultType: string;
  id: string;
  render_id: string;
  gene_id?: string;
}

interface SearchResult {
  label: React.ReactNode;
  value: SearchChoice;
}

const MobileSearchButton = styled.button`
  display: none;
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.65);
  border-radius: 8px;
  background: transparent;
  color: white;
  cursor: pointer;
  font-size: 20px;

  &:focus-visible {
    outline: 2px solid white;
    outline-offset: 2px;
  }

  @media (max-width: 750px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`

const HeaderSearchBarContainer = styled(SearchBarContainer)<{ $compactOnMobile: boolean }>`
  ${({ $compactOnMobile }) =>
    $compactOnMobile &&
    `
      @media (max-width: 750px) {
        display: none;
      }
    `}
`

interface NewSearchBarProps {
  compactOnMobile?: boolean
}

export const NewSearchBar: React.FC<NewSearchBarProps> = ({ compactOnMobile = false }) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const geneSymbols = useRecoilValue(geneSymbolsQuery);
  const analysisMetadata = useRecoilValue(filteredAnalysesQuery);
  const setSelectedAnalyses = useSetRecoilState(selectedAnalyses);
  const { goToGene, goToPhenotype, goToVariant } = useAppNavigation();
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileTriggerRef = useRef<HTMLButtonElement>(null);
  const highlightedRef = useRef<HTMLDivElement>(null);

  const modalInputRef = useRef<HTMLInputElement>(null);

  const focusSearchBar = useCallback(
    (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (e.key === '/' && !e.metaKey && !e.ctrlKey) {
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        e.preventDefault();
        setShowModal(true);
        setTimeout(() => modalInputRef.current?.focus(), 0);
      }
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowModal(true);
        setTimeout(() => modalInputRef.current?.focus(), 0);
      }
    },
    [inputRef]
  );

  // Debounce input value for async search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, 250);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch results when debounced value changes
  useEffect(() => {
    let active = true;

    const fetchResults = async () => {
      const query = debouncedValue.trim();
      if (!query) {
        if (active) {
          setShowModal(false);
          setSearchResults([]);
        }
        return;
      }

      const geneResults = searchGeneSymbols(query, geneSymbols.data || []);
      const analysisResults = searchAnalysisMetadata(query, analysisMetadata || []);
      let varResults: SearchResult[] = [];

      // Check if query looks like a variant coordinate
      if (/^(chr)?(\d+|x|y|m|mt)[-:]\d+/i.test(query)) {
        try {
          const res = await axios.get(`${axaouDevUrl}/variants/search`, { params: { q: query } });
          if (active && res.data) {
             varResults = res.data.map((v: any) => {
               const sep = <Separator>·</Separator>;
               const elements: React.ReactNode[] = [];

               // Variant ID in monospace
               elements.push(<VariantId key="vid">{v.variant_id}</VariantId>);

               // Gene symbol (italic, blue) + consequence (with color dot)
               if (v.gene_symbol) {
                 elements.push(sep);
                 elements.push(<GeneSymbolLabel key="gene">{v.gene_symbol}</GeneSymbolLabel>);
               }
               if (v.consequence) {
                 elements.push(sep);
                 elements.push(
                   <ConsequenceLabel key="csq">
                     <ColorMarker color={getConsequenceColor(v.consequence)} />
                     {getLabelForConsequenceTerm(v.consequence)}
                   </ConsequenceLabel>
                 );
               }

               // Association info: phenotype + colored p-value
               if (v.top_phenotype) {
                 elements.push(sep);
                 const pColor = v.top_pvalue != null
                   ? v.top_pvalue < variantGreenThreshold ? greenThresholdColor
                     : v.top_pvalue < variantYellowThreshold ? yellowThresholdColor
                     : undefined
                   : undefined;
                 elements.push(
                   <AssocInfo key="assoc">
                     {v.top_phenotype}
                     {v.top_pvalue != null && (
                       <>
                         {' '}
                         <ColorMarker color={pColor || 'white'} border="solid" borderColor="black" />
                         {v.top_pvalue === 0 ? '< 1e-300' : Number(v.top_pvalue.toPrecision(2)).toExponential()}
                       </>
                     )}
                     {v.num_associations > 1 && ` + ${v.num_associations - 1} more`}
                   </AssocInfo>
                 );
               }

               return {
                 label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 0, flexWrap: 'wrap' }}>{elements}</span>,
                 value: { resultType: 'variant', id: v.variant_id, render_id: v.variant_id, gene_id: v.gene_id }
               };
             });
          }
        } catch (e) {
          console.error('Failed to fetch variants for search:', e);
        }
      }

      if (active) {
        setSearchResults([...geneResults, ...analysisResults, ...varResults]);
        setShowModal(true);
      }
    };

    fetchResults();

    return () => {
      active = false;
    };
  }, [debouncedValue, geneSymbols.data, analysisMetadata]);

  useEffect(() => {
    window.addEventListener('keydown', focusSearchBar);
    return () => {
      window.removeEventListener('keydown', focusSearchBar);
    };
  }, [focusSearchBar]);

  useEffect(() => {
    if (searchResults.length > 0) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(null);
    }
  }, [searchResults]);

  useEffect(() => {
    if (highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const searchGeneSymbols = (
    query: string,
    geneSymbols: GeneSymbol[]
  ): SearchResult[] => {
    return geneSymbols
      .filter((symbol) =>
        symbol?.gene_symbol?.toLowerCase()?.includes(query.toLowerCase())
      )
      .sort((a, b) => {
        const queryLower = query.toLowerCase();
        const aSymbol = (a.gene_symbol || '').toLowerCase();
        const bSymbol = (b.gene_symbol || '').toLowerCase();

        if (aSymbol.startsWith(queryLower) && !bSymbol.startsWith(queryLower)) {
          return -1;
        }
        if (!aSymbol.startsWith(queryLower) && bSymbol.startsWith(queryLower)) {
          return 1;
        }
        if (aSymbol === queryLower && bSymbol !== queryLower) {
          return -1;
        }
        if (aSymbol !== queryLower && bSymbol === queryLower) {
          return 1;
        }
        return 0;
      })
      .map((symbol) => ({
        label: symbol.gene_symbol,
        value: { resultType: 'gene', id: symbol.gene_id, render_id: symbol.gene_id },
      }));
  };

  const searchAnalysisMetadata = (
    query: string,
    analysisMetadata: AnalysisMetadata[]
  ): SearchResult[] => {
    return analysisMetadata
      .filter(
        (metadata) =>
          (metadata.analysis_id || '').toLowerCase().includes(query.toLowerCase()) ||
          (metadata.category || '').toLowerCase().includes(query.toLowerCase()) ||
          (metadata.description || '').toLowerCase().includes(query.toLowerCase())
      )
      .map((metadata) => ({
        label: `${metadata.analysis_id} - ${metadata.description}`,
        value: { resultType: 'analysis', id: metadata.analysis_id, render_id: uuidv4() },
      }));
  };

  const onSelect = (searchChoice: SearchChoice) => {
    if (searchChoice.resultType === 'gene') {
      goToGene(searchChoice.id, {
        destination: 'phewas',
        resultIndex: 'gene-phewas',
        resultsOnly: true,
      });
    } else if (searchChoice.resultType === 'analysis') {
      goToPhenotype(searchChoice.id, {
        destination: 'overview',
        resultIndex: 'pheno-info',
        resultsOnly: true,
      });
      setSelectedAnalyses([]);
    } else if (searchChoice.resultType === 'variant') {
      goToVariant(searchChoice.id, {
        destination: 'phewas',
        geneId: searchChoice.gene_id || null,
        regionId: null,
        resultIndex: 'variant-phewas',
        resultsOnly: true,
      });
    }
    setShowModal(false);
    setInputValue('');
  };

  const openSearch = () => {
    setShowModal(true);
    setTimeout(() => modalInputRef.current?.focus(), 0);
  };

  const closeSearch = () => {
    setShowModal(false);
    setTimeout(() => {
      if (compactOnMobile && window.matchMedia('(max-width: 750px)').matches) {
        mobileTriggerRef.current?.focus();
      } else {
        inputRef.current?.focus();
      }
    }, 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleClearInput = () => {
    setInputValue('');
    setShowModal(false);
    setSearchResults([]);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showModal) {
      if (e.key === 'Tab' && searchResults.length > 0) {
        e.preventDefault();
        setShowModal(true);
        setHighlightedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex === null || prevIndex === searchResults.length - 1
            ? 0
            : prevIndex + 1
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex === null || prevIndex === 0
            ? searchResults.length - 1
            : prevIndex - 1
        );
        break;
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          setHighlightedIndex((prevIndex) =>
            prevIndex === null || prevIndex === 0
              ? searchResults.length - 1
              : prevIndex - 1
          );
        } else {
          setHighlightedIndex((prevIndex) =>
            prevIndex === null || prevIndex === searchResults.length - 1
              ? 0
              : prevIndex + 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex !== null && highlightedIndex >= 0 && searchResults[highlightedIndex]) {
          onSelect(searchResults[highlightedIndex].value);
        } else {
          setShowModal(false);
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeSearch();
        setSearchResults([]);
        break;
      default:
        break;
    }
  };

  const geneResults = searchResults.filter(
    (result) => result.value.resultType === 'gene'
  );
  const analysisResults = searchResults.filter(
    (result) => result.value.resultType === 'analysis'
  );
  const variantResults = searchResults.filter(
    (result) => result.value.resultType === 'variant'
  );

  return (
    <>
      <HeaderSearchBarContainer $compactOnMobile={compactOnMobile}>
        <Icon aria-hidden="true">🔍</Icon>
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search by gene, phenotype, or variant"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          aria-autocomplete="list"
          aria-expanded={showModal}
          aria-controls={mobileControlContract.globalSearch.controlsId}
        />
        {inputValue && <CloseIcon onClick={handleClearInput}>✖</CloseIcon>}
      </HeaderSearchBarContainer>
      {compactOnMobile && (
        <MobileSearchButton
          ref={mobileTriggerRef}
          type="button"
          aria-label={mobileControlContract.globalSearch.triggerLabel}
          aria-expanded={showModal}
          aria-controls={mobileControlContract.globalSearch.controlsId}
          onClick={openSearch}
          className="mobile-global-search-button"
        >
          <span aria-hidden="true">🔍</span>
        </MobileSearchButton>
      )}
      {showModal && (
        <>
        <ModalBackdrop onClick={closeSearch} />
        <ModalContainer
          role="dialog"
          aria-modal="true"
          aria-label="Global search"
          id={mobileControlContract.globalSearch.controlsId}
        >
          <CloseModalButton
            type="button"
            aria-label="Close global search"
            onClick={closeSearch}
          >
            ✖
          </CloseModalButton>
          <QueryBox>
            <Magnifier>🔍</Magnifier>
            <ModalInput
              ref={modalInputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Search by gene, phenotype, or variant"
              autoFocus
            />
          </QueryBox>
          {geneResults.length > 0 && (
            <>
              <SectionTitle>Genes</SectionTitle>
              {geneResults.map((result) => {
                const globalIndex = searchResults.indexOf(result);
                return (
                  <div
                    key={`gene-${result.value.render_id}`}
                    ref={highlightedIndex === globalIndex ? highlightedRef : undefined}
                    style={{
                      padding: '10px',
                      cursor: 'pointer',
                      backgroundColor:
                        highlightedIndex === globalIndex ? 'var(--theme-border)' : 'transparent',
                    }}
                    onClick={() => onSelect(result.value)}
                    onMouseEnter={() => setHighlightedIndex(globalIndex)}
                    role="option"
                    aria-selected={highlightedIndex === globalIndex}
                  >
                    {result.label}
                  </div>
                );
              })}
            </>
          )}
          {analysisResults.length > 0 && (
            <>
              <SectionTitle>Analyses</SectionTitle>
              {analysisResults.map((result) => {
                const globalIndex = searchResults.indexOf(result);
                return (
                  <div
                    key={`analysis-${result.value.render_id}`}
                    ref={highlightedIndex === globalIndex ? highlightedRef : undefined}
                    style={{
                      padding: '10px',
                      cursor: 'pointer',
                      backgroundColor:
                        highlightedIndex === globalIndex ? 'var(--theme-border)' : 'transparent',
                    }}
                    onClick={() => onSelect(result.value)}
                    onMouseEnter={() => setHighlightedIndex(globalIndex)}
                    role="option"
                    aria-selected={highlightedIndex === globalIndex}
                  >
                    {result.label}
                  </div>
                );
              })}
            </>
          )}
          {variantResults.length > 0 && (
            <>
              <SectionTitle>Variants</SectionTitle>
              {variantResults.map((result) => {
                const globalIndex = searchResults.indexOf(result);
                return (
                  <div
                    key={`variant-${result.value.render_id}`}
                    ref={highlightedIndex === globalIndex ? highlightedRef : undefined}
                    style={{
                      padding: '10px',
                      cursor: 'pointer',
                      backgroundColor:
                        highlightedIndex === globalIndex ? 'var(--theme-border)' : 'transparent',
                    }}
                    onClick={() => onSelect(result.value)}
                    onMouseEnter={() => setHighlightedIndex(globalIndex)}
                    role="option"
                    aria-selected={highlightedIndex === globalIndex}
                  >
                    {result.label}
                  </div>
                );
              })}
            </>
          )}
        </ModalContainer>
        </>
      )}
    </>
  );
};

export default NewSearchBar;

